import { useState, useEffect } from 'react';
import { engagementsApi } from '../../../api/engagements.js';
import { buildMailtoLink } from '../../../utils/mailto.js';
import { useSender } from '../../../hooks/useSender.js';
import { Modal } from '../../../components/common/Modal.jsx';
import { FormField, Select, Textarea } from '../../../components/common/FormField.jsx';
import { ErrorAlert } from '../../../components/common/ErrorAlert.jsx';
import { formatDate } from '../../../utils/dates.js';
import { COMM_TYPES } from '../../../utils/constants.js';

export function CommsTab({ engagement, event, speaker }) {
  const sender = useSender();
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draftModal, setDraftModal] = useState(false);
  const [selectedType, setSelectedType] = useState(COMM_TYPES[0]);
  const [error, setError] = useState(null);

  useEffect(() => {
    engagementsApi.listCommunications(engagement.id)
      .then(setLog)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [engagement.id]);

  const openDraft = (type) => {
    setSelectedType(type);
    setDraftModal(true);
  };

  const logAndOpen = async (type) => {
    const mailto = buildMailtoLink(type, engagement, event, speaker, sender);
    // Log the comm
    try {
      const entry = await engagementsApi.logCommunication(engagement.id, {
        commType: type,
        recipient: speaker?.email ?? '',
        date: new Date().toISOString().slice(0, 10),
      });
      setLog((prev) => [...prev, entry]);
    } catch { /* non-blocking */ }
    window.location.href = mailto;
    setDraftModal(false);
  };

  return (
    <div className="stack">
      <ErrorAlert error={error} onDismiss={() => setError(null)} />

      <div className="between">
        <h3 style={{ fontSize: 'var(--text-base)' }}>Communication log</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setDraftModal(true)}>
          Generate email draft
        </button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>Loading…</p>
      ) : log.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>No communications logged yet.</p>
      ) : (
        <div className="data-table" style={{ overflowX: 'auto' }}>
          <table>
            <thead><tr><th>Type</th><th>Recipient</th><th>Date</th><th>Note</th></tr></thead>
            <tbody>
              {[...log].reverse().map((entry) => (
                <tr key={entry.id}>
                  <td style={{ fontSize: 'var(--text-sm)' }}>{entry.commType}</td>
                  <td style={{ fontSize: 'var(--text-sm)' }}>{entry.recipient}</td>
                  <td style={{ fontSize: 'var(--text-sm)' }}>{formatDate(entry.date)}</td>
                  <td style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{entry.note ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={draftModal}
        onClose={() => setDraftModal(false)}
        title="Generate email draft"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setDraftModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={() => logAndOpen(selectedType)}>
              Open in mail client
            </button>
          </>
        }
      >
        <div className="stack">
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            Select a template. The draft will open in your mail client with recipient, subject, and body pre-filled. You can edit before sending.
          </p>
          <FormField label="Template" id="commType">
            <Select id="commType" value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
              {COMM_TYPES.filter((t) => t !== 'Other').map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </Select>
          </FormField>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            Clicking "Open in mail client" will also log this communication automatically.
          </p>
        </div>
      </Modal>
    </div>
  );
}
